import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatCurrency } from "@/utils/format/currency";
import { ProRataMemoryData } from "@/hooks/use-po-contract-prorata-memory/use-po-contract-prorata-memory.types";

const EMPTY_VALUE = "-";

/**
 * Formata um número no padrão pt-BR (vírgula decimal), como o resto do BKO.
 * Valores ausentes ou não numéricos viram "-" em vez de "NaN"/"undefined".
 */
const formatNumber = (value?: number, maximumFractionDigits = 2) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return EMPTY_VALUE;

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);
};

const formatMoney = (value?: number) =>
  typeof value === "number" && Number.isFinite(value)
    ? formatCurrency(value)
    : EMPTY_VALUE;

/**
 * Mensagens de erro da API nem sempre terminam em pontuação ("Pacote não
 * encontrado"), e o aviso abaixo emenda uma segunda frase logo em seguida.
 * Sem isso o operador leria "Pacote não encontrado Você pode...".
 */
const asSentence = (message: string) => {
  const trimmed = message.trim();

  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const formatPercentage = (value?: number) => {
  const formatted = formatNumber(value);

  return formatted === EMPTY_VALUE ? EMPTY_VALUE : `${formatted}%`;
};

export type PrivateOfficeProRataMemoryProps = {
  data: ProRataMemoryData | null;
  isLoading?: boolean;
  error?: string | null;
  /** Ação que o operador ainda pode concluir mesmo com a prévia indisponível. */
  pendingActionLabel?: string;
};

export const PrivateOfficeProRataMemory = ({
  data,
  isLoading = false,
  error = null,
  pendingActionLabel = "concluir a edição",
}: PrivateOfficeProRataMemoryProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent
          className="p-4 text-sm text-muted-foreground"
          aria-live="polite"
        >
          Calculando a memória do pró-rata...
        </CardContent>
      </Card>
    );
  }

  // A prévia é informativa e nunca bloqueia a confirmação da edição, mas o
  // operador precisa saber que ela falhou: sem este aviso, uma falha de API
  // seria indistinguível de "este contrato não tem pró-rata".
  if (error) {
    return (
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="p-4 text-sm text-amber-900" role="status">
          {asSentence(error)} Você pode {pendingActionLabel} normalmente — o
          cálculo do pró-rata é feito pelo sistema ao salvar.
        </CardContent>
      </Card>
    );
  }

  // A memória de pró-rata só faz sentido quando há, de fato, pró-rata a
  // exibir. Para "ciclo_completo" e "sem_valor" o fluxo de confirmação
  // segue normal, sem essa etapa.
  if (!data || data.situacao !== "prorata") {
    return null;
  }

  const items: { label: string; value: string }[] = [
    { label: "Créditos do mês", value: formatNumber(data.creditosContrato) },
    { label: "Dias do ciclo", value: formatNumber(data.diasCiclo, 0) },
    { label: "Dias cobrados", value: formatNumber(data.diasCobrados, 0) },
    { label: "Créditos pró-rata", value: formatNumber(data.creditosProrata) },
    { label: "Preço do crédito", value: formatMoney(data.precoCredito) },
    {
      label: "Valor de referência",
      value: formatMoney(data.valorProrata),
    },
    { label: "Take rate", value: formatPercentage(data.takePercentage) },
    {
      label: "Repasse ao parceiro",
      value: formatMoney(data.repasseParceiro),
    },
  ];

  return (
    <Card className="border-amber-200 bg-amber-50/40">
      <CardHeader>
        <CardTitle className="text-lg font-medium">
          Memória de cálculo do pró-rata
        </CardTitle>
        <CardDescription>
          No ciclo de entrada, este contrato consome créditos proporcionais
          (pró-rata) do pacote: os créditos do mês são divididos pelo número
          real de dias do ciclo e multiplicados pelos dias de vigência no ciclo.
          As parcelas do pacote não mudam — o valor abaixo é a referência do
          consumo proporcional e a base do repasse ao parceiro. Por isso, dois
          contratos iniciados no mesmo dia em meses diferentes (por exemplo, dia
          10 de um mês com 28 dias e dia 10 de um mês com 31 dias) podem
          resultar em créditos e valores diferentes — isso é esperado e não é um
          erro de cálculo.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {items.map((item) => (
            <div key={item.label}>
              <dt className="text-sm text-muted-foreground">{item.label}</dt>
              <dd className="font-medium text-foreground">{item.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};
